/*
*  home.js
*  Project: Kanoogi Platform - Main Landing Page
*
*  Created by Chris Taylor on Feb 9, 2021
 *  Copyright 2021-2026 Kanoogi Inc. | All Rights Reserved.
*
*/

'use strict';

const kanoogi=window.kanoogi||{};									// Create one Kanoogi object to contain the whole shooting match
window.kanoogi=kanoogi;												// Store the master Kanoogi object into the global window object

import './style.css';
import './home.css';

import * as Module_utils from '../../Kanlibs/KAN_Utils.js';
// import * as Module_draw from '../../Kanlibs/KAN_Draw.js';
// import * as Module_network from '../../Kanlibs/KAN_Network.js';

import * as Module_logo3d from '../../Kanlibs/KAN_Logo3D.js';		// WebGL home-page logo; silently falls back to SVG if unavailable
import * as Module_signedin from './signedin.js';					// GetJWT() — the synchronous "is there a token" marker, used to skip the logo intro for returning users
import * as optionpanel from './optionpanel.js';					// Handles various tasks related to the options panel (should rename)

// kanoogi.draw=new Module_draw.KAN_Draw();							// Init Kanoogi Draw Routines (includes WebGL)
// kanoogi.network=new Module_network.KAN_Network();					// Init Kanoogi Networking

const utils=kanoogi.utils||new Module_utils.KAN_Utils();			// Initialize a new utils module if it doesn't exist
window.kanoogi.utils=utils;											// Store the master Kanoogi utils object into the global window object

const local=utils.RunningLocally();									// Set local var TRUE if running locally

kanoogi.optionpanel=false;											// Default to signed out

// Init Game UI System with Kanoogi libs (special case for this initialization)
// I commented out this code so it was here for instructional purposes

//	kanoogi.kangameui=new KAN_GameUI(kanoogi.kaninput,kanoogi.kandraw,kanoogi.kanaudio);
//	kanoogi.geometry=new KAN_Geometry(utils);						// Init the Kanoogi Geometry System

/* ---------------------------------------------------------------- */

/* ---------------------------------------------------------------- */

// RevealTagline() - Fly the tagline in one word at a time.
//
// The sentence is SVG OUTLINES (src/tagline.html), one <g class="tw"> per word,
// generated from the Bangers TTF by tools/tagline/make_tagline.py.  It used to be
// a plain <div> in font-family:'Bangers' with no fallback, so a failed webfont
// fetch left the browser substituting its own default -- a different face on every
// OS.  Outlines have no font dependency, so it is now identical everywhere.
//
// The motion: each word zips in from the left, overshoots slightly and settles;
// as it lands it KNOCKS the word before it, which springs back just as the next
// word arrives.  Reads as a sentence being assembled rather than a block appearing.
//
// Called at exactly the moment the old fade was -- when the 3D logo finishes
// lighting up (~4s), plus the no-WebGL fallback timer.  Both paths call this and
// it is idempotent: the .reveal class is the latch, so a second call is a no-op
// rather than a restart mid-flight.
//
// prefers-reduced-motion is ALSO handled in home.css, so the finished sentence
// still shows even if this function never runs at all.

// PACING. GAP is now 125ms, deliberately SHORTER than the 170ms flight, so this no
// longer runs one-word-at-a-time: ~1.4 words are in the air at any moment and the row
// is still settling from the last knock when the next word launches. That is a chosen
// trade for a ~1s assembly, not an oversight -- the earlier one-at-a-time rule and its
// arithmetic are kept below for whoever widens GAP again.
//
// What is NOT negotiable is that words never overlap, and they still don't: entering
// from the RIGHT in reading order, each arrival's home lies right of everything already
// placed, and an earlier word targets a home further left so it travels farther and
// stays ahead of the one behind it. Verified at this cadence -- worst on-screen overlap
// 0.000 units over a 0.5ms sweep of the real per-word bounds.
//
// The one thing that WOULD break visibly is KNOCK >= GAP: consecutive knocks would then
// overlap in time, and since only the first active one is applied the row would pop as
// it handed over. At 110ms against a 125ms spacing there is 15ms clear.
//
// The original one-at-a-time rule, if GAP ever goes back up:
//   GAP >= ZIP*0.82 + KNOCK   keeps the row still between arrivals (needs GAP >= 250ms)
//   GAP >  ZIP                keeps exactly one word airborne   (needs GAP >  170ms)
//   lands at        start + ZIP            = 170ms
//   knock settles   start + ZIP*0.82+KNOCK = 249ms
//   next starts     start + GAP            = 125ms   <- BEFORE the above, by design now
//
// WHOLE SENTENCE = 7*GAP + ZIP = 1045ms.
const TAGLINE_ZIP=170;												// ms for one word to fly in
const TAGLINE_GAP=125;												// ms between word STARTS - now SHORTER than ZIP, so ~1.4 words are airborne at once
const TAGLINE_KNOCK=110;											// ms for a knocked word to recoil and spring back - must fit inside GAP - ZIP*0.82

// LOGO_INTRO_SEEN - the logo's 4-second dark->lit reveal is a first-impression
// piece, and after the first impression it is just four seconds of waiting. It
// now plays ONCE PER BROWSER, and never at all for a signed-in user, who by
// definition has been here before.
//
// The TAGLINE is deliberately not gated: it re-zips on every load, including
// reloads. Only the logo's fade is once-only.
const LOGO_INTRO_SEEN='kan_logo_intro_seen';

// Decided SYNCHRONOUSLY, because it selects the very first frame's lighting --
// there is no time to ask the server. signedin.GetJWT() reads localStorage,
// which is the same marker the rest of the site treats as "signed in"
// (signedin.SignedIn() round-trips /validate and resolves far too late to
// gate a first paint). A stale token therefore skips the intro for someone
// whose session has actually expired; that is the right trade for a decorative
// fade, and it errs toward the quieter behaviour.
function ShouldPlayLogoIntro()
	{
	try { if (Module_signedin.GetJWT()) return false; }				// signed in -> they have seen it
	catch (_) { /* storage blocked: fall through and treat as anonymous */ }

	let seen=false;
	try { seen=window.localStorage.getItem(LOGO_INTRO_SEEN)==='1'; }
	catch (_) { /* private mode / storage disabled -> no memory, so play it */ }
	if (seen) return false;

	// Stamped on the DECISION, not on completion: someone who lands and leaves
	// after two seconds has had the impression, and replaying it on their next
	// visit would defeat the point. Failure to write is non-fatal -- worst case
	// the intro plays again.
	try { window.localStorage.setItem(LOGO_INTRO_SEEN,'1'); } catch (_) {}
	return true;
	}

// 3D entrance. Same numbers as the 2D constants above, in seconds, because the
// pacing rule is identical and it is the whole point: one word at a time.
// flyStagger is a gap BETWEEN releases -- the renderer's own default is a fixed
// TOTAL spread that compresses as unit count rises, which at 8 words would put
// 4-5 of them in the air together.
// Words enter from the RIGHT, in reading order. That pairing is the only one that
// assembles this sentence without a word ever crossing one already placed: each
// arrival's home lies to the right of the standing row, so it flies in behind the
// row and stops on contact. (Entering from the left forces the opposite --
// reading order would have every arrival plough through the whole sentence.)
// flyKnock is in OBJECT units (the mesh is ~840 wide), so 15 is ~1.8% of the
// sentence -- the same proportion as the 16px shove on the ~900px flat SVG.
const TAGLINE_3D_OPTS={
	objUrl:'/models/tagline.obj',
	units:'auto',													// 8 OBJ objects w0..w7, in reading order -> 8 units
	intro:'flyin',
	baseMode:'flyin',
	flyFrom:'right',												// entering from the RIGHT is what lets the sentence keep reading order: each arrival's home is to the right of everything standing, so it never crosses it
	flyNoOvershoot:true,											// stop dead on contact -- the elastic default rings ~3.6 word-widths past home, straight through the neighbour
	flyDuration:TAGLINE_ZIP/1000,
	flyStagger:TAGLINE_GAP/1000,
	flyKnock:15,
	flyKnockDur:TAGLINE_KNOCK/1000,									// same number the 2D fallback uses, rather than the renderer's 0.15 default which would overrun this cadence
	lighting:{lx:0.4,ly:0.6,lz:1.0,ambient:0.45,specStrength:0.25,shininess:32},
	};

function RevealTagline()
	{
	const el=document.querySelector('.indiegaming');
	if (!el || el.classList.contains('reveal')) return;				// latch: never restart a run already in flight
	el.classList.add('reveal');										// container must be visibility:visible for the 3D path too -- the renderer skips a placeholder whose parent is hidden

	const words=el.querySelectorAll('.tw');
	if (!words.length) return;										// outlines missing -> CSS alone still reveals it

	// Respect the OS setting here too. CSS pins the end state regardless, but
	// without this we would still schedule a pile of invisible work.
	if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

	// Preferred path: the same extruded-mesh renderer the logo uses, so the
	// sentence is block letters in 3D rather than flat fill. The SVG stays in
	// the document as the layout placeholder -- the renderer reads its screen
	// rect every frame and projects the mesh exactly there, and the canvas is a
	// full-viewport overlay, so words can enter from the real screen edge
	// instead of being clipped by the sentence's own box.
	//
	// Hide the SVG BEFORE the fetch, not after: InitLogo3D has to pull ~100KB
	// of OBJ, and without this the flat sentence would sit visible for that
	// whole time and then blink out as the canvas took over.
	const svg=el.querySelector('svg');
	if (svg && Module_logo3d && Module_logo3d.InitLogo3D)
		{
		svg.style.visibility='hidden';
		const Fallback=()=>{ svg.style.visibility=''; RevealTagline2D(words); };
		let p;
		try { p=Module_logo3d.InitLogo3D(svg,TAGLINE_3D_OPTS); }
		catch (_) { Fallback(); return; }
		Promise.resolve(p).then(ok=>{ if (!ok) Fallback(); },Fallback);
		return;
		}

	RevealTagline2D(words);
	}

// The flat-SVG entrance. Still the fallback whenever the 3D path can't run --
// no WebGL, model fetch failed, parse failed. Same choreography, 2D.
function RevealTagline2D(words)
	{
	// Web Animations API rather than CSS keyframes: the delays are computed per
	// word, and each knock has to be scheduled off the NEXT word's arrival.
	words.forEach((w,i)=>
		{
		const start=i*TAGLINE_GAP;

		w.animate(
			[
			{offset:0,    transform:'translateX(-1400px)', opacity:0},
			{offset:0.55,                                  opacity:1},
			{offset:0.85, transform:'translateX(28px)',    opacity:1},	// the overshoot IS the impact
			{offset:1,    transform:'translateX(0)',       opacity:1}
			],
			{duration:TAGLINE_ZIP,delay:start,easing:'cubic-bezier(.2,.8,.3,1)',fill:'both'});

		const prev=words[i-1];										// knock the word before, at the moment this one lands
		if (prev)
			prev.animate(
				[
				{offset:0,   transform:'translateX(0)'},
				{offset:0.4, transform:'translateX(16px) rotate(1.2deg)'},
				{offset:1,   transform:'translateX(0) rotate(0)'}
				],
				{duration:TAGLINE_KNOCK,delay:start+TAGLINE_ZIP*0.82,easing:'cubic-bezier(.34,1.56,.64,1)',fill:'none'});
		});
	}

/* ---------------------------------------------------------------- */

async function LoadWebassembly()
	{
	const wasmUrl=new URL('./c_code/wasm/wasm_test.wasm',import.meta.url);

    console.debug("index.js is running... initiating WebAssembly loading.");	// debug (not log): WASM smoke-test diagnostic, hidden from regular users at default DevTools log level

	const memory=new WebAssembly.Memory({initial:100,maximum:1000});
	const heap=new Uint8Array(memory.buffer);

	heap[1]=142;

	const importObject=
		{
		env:
			{
			console_log:(arg,arg2)=>{console.debug(arg,arg2);},			// WASM bridge — debug so the test module's emit ("40 0") doesn't show in regular users' consoles
			memory:memory,
			}
		};

	try {
		const {instance}=await WebAssembly.instantiateStreaming(fetch(wasmUrl),importObject);

		const start=Date.now();
		instance.exports.TestFunction(40);
		const end=Date.now();
		console.debug(`🟢 WebAssembly Execution time: ${end-start} ms`);	// debug: smoke-test timing, dev-only
		}
	catch (error)
		{
		console.error("🔴 Failed to load WebAssembly module 'wasm_test.wasm'. Check file path and compilation:",error);
		}
	}
/* ---------------------------------------------------------------- */

// This code is a 'one shot' that takes place 10ms after the page loads.  This was initially just to turn the page on
// to avoid a nasty flicker, but then has added functionality to set the home button to being active with an outline.

(async()=>
	{
	utils.KanoogiTextLogo();

	// Promote the SVG logo to a WebGL rendering of the extruded mesh.
	// Per-page lighting, WHEN THE INTRO PLAYS: starts pitch-dark (back-lit
	// only, ambient=0), the room lights "come up" over ~2s, then Light Z
	// pulses front/back every 4 seconds — the logo seems to breathe under
	// shifting light. When the intro is skipped (signed in, or already seen
	// once in this browser) stages 1-2 don't run at all: the logo opens lit
	// and breathing, and the tagline follows at 0.5s instead of 4s.
	// Falls back to <img> if WebGL or the model isn't available.
	let taglineRevealed=false;										// one-shot guard for the tagline fade-in
	// Whether to play the dark->lit intro at all. See ShouldPlayLogoIntro():
	// signed-in users never get it, anonymous users get it once per browser.
	const playLogoIntro=ShouldPlayLogoIntro();
	// When the intro is skipped the logo is already lit, so there is nothing to
	// wait for -- the tagline goes at 0.5s instead of 4s. Not 0: the tagline's
	// own ~105KB model fetch would otherwise contend with the logo's first paint.
	const TAGLINE_AT=playLogoIntro?4:0.5;							// seconds
	const logoImg=document.getElementById('kanlogo');
	if (logoImg) Module_logo3d.InitLogo3D(logoImg,
		{
		intro:'static',												// no rubber-band on page load — logo just appears in position
		clickAxisCycle:true,
		settleAfter:4,												// seconds of no clicking before the logo eases back to its opening pose. Clicking mid-spin banks the partial rotation permanently, so without this a burst of fast clicks leaves it stuck at an arbitrary angle.										// click 1 spins X, click 2 spins Y, click 3 spins Z, then cycles.  The previous baseMode:'flyin' is gone — no more blast on click.
		// Start dark only if we are actually going to play the reveal; otherwise
		// open on the settled stage-3 values so there is no flash of black.
		lighting:{lx:0.4,ly:0.6,lz:-1.0,ambient:playLogoIntro?0:0.5,specStrength:0.25,shininess:32},
		lightingAnimate:(t,L)=>
			{
			if (!playLogoIntro)
				{
				// Seen it / signed in: skip stages 1-2 entirely and run stage 3
				// from t=0, so the breathing starts immediately and the logo is
				// lit on the first frame.
				L.ambient=0.5;
				L.lz=-Math.cos(t*Math.PI/4);
				if (!taglineRevealed && t>=TAGLINE_AT)
					{
					taglineRevealed=true;
					RevealTagline();
					}
				return;
				}
			if (t<2)
				{
				// Stage 1 (0-2s): all dark — light from behind, no ambient.
				L.ambient=0;
				L.lz=-1;
				}
			else if (t<4)
				{
				// Stage 2 (2-4s): ambient ramps 0 -> 0.5 — logo emerges.
				L.ambient=((t-2)/2)*0.5;
				L.lz=-1;
				}
			else
				{
				// Stage 3 (4s+): ambient steady at 0.5; Light Z gently
				// pulses from -1 up to +1 and back to -1 every 8s
				// (4s rising, 4s falling) — subtle breathing.
				L.ambient=0.5;
				L.lz=-Math.cos((t-4)*Math.PI/4);
				// One-shot: when stage 3 begins (logo finished lighting up
				// and is just starting to glow), fade in the tagline.
				if (!taglineRevealed)
					{
					taglineRevealed=true;
					RevealTagline();							// word-by-word entrance (was: add .reveal for a 2s fade)
					}
				}
			},
		});

	// Fallback for the tagline fade-in: the WebGL renderer's lightingAnimate
	// normally triggers .reveal at t=4s, but if WebGL isn't available (no
	// renderer, no animator) we still want the tagline to appear. Class adds
	// are idempotent so the two paths don't fight when both fire.
	//
	// Registered BEFORE the ValidateLogin() await, not after: that await is a
	// network round-trip, and this codebase has already been bitten by a
	// promise in the auth path that never settles. When it hung, every
	// statement below it -- including this timer -- simply never ran, and the
	// tagline stayed invisible for the life of the page. A timer that exists
	// to survive failure must not be scheduled behind the thing that fails.
	setTimeout(()=>
		{
		RevealTagline();									// same entrance; RevealTagline latches so the two paths can't double-fire
		},TAGLINE_AT*1000+500);								// ~4.5s when the intro plays, ~1s when it is skipped

	await optionpanel.ValidateLogin();
	optionpanel.MenuState(true);

	document.getElementById('home_nav').classList.add('active');

	const renderPageID=document.getElementById('renderpage');
	if (renderPageID) renderPageID.style.visibility='visible';		// Activate the entire page so it's visible

	if (typeof WebAssembly==="object")
		{
		if (true)
			{
			await LoadWebassembly();
			console.debug('🟢 WebAssembly Test Complete');				// debug: smoke-test completion marker, dev-only
			}
		}
	})();
